import type { ChangeEvent } from 'react';
import React from 'react';
import { RadioWithLabel } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import Link from 'next/link';

import * as styles from './styles';

const doNothing = () => undefined;

type RadioButtonLinkData = {
  checked: boolean;
  disabled?: boolean;
  href?: string;
  labelText: string;
  secondaryLabelText?: string;
  value: string;
};

export interface RadioButtonLinkGroupProps {
  accessibleLabel: string;
  accessibleLabelElementId?: string;
  accessibleLabelElementType?: keyof JSX.IntrinsicElements;
  hideLabel?: boolean;
  name: string;
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void;
  radioButtonData: RadioButtonLinkData[];
}

export const RadioButtonLinkGroup = ({
  name,
  accessibleLabel,
  accessibleLabelElementType: AccessibleLabelElementTag = 'p',
  accessibleLabelElementId = `${name}-label`,
  hideLabel = false,
  onChange,
  radioButtonData,
}: RadioButtonLinkGroupProps) => (
  <div aria-labelledby={accessibleLabelElementId} role="group">
    <span css={hideLabel ? visuallyHidden : undefined} id={accessibleLabelElementId}>
      <AccessibleLabelElementTag>{accessibleLabel}</AccessibleLabelElementTag>
    </span>
    {radioButtonData.map(
      ({ checked, disabled, href, labelText, secondaryLabelText, value: radioButtonValue }) => {
        const showLink = Boolean(href && !disabled);
        const linkIsPrimaryInteractive = showLink && !onChange;
        const accessibleLinkName = `${labelText}${
          secondaryLabelText ? ` ${secondaryLabelText}` : ''
        }`;

        const linkedRadioButton = (
          <>
            <Link
              aria-current={linkIsPrimaryInteractive && checked ? 'true' : undefined}
              aria-hidden={linkIsPrimaryInteractive ? undefined : 'true'}
              css={styles.linkUnderlay}
              data-testid={`${name}-${radioButtonValue}-link`}
              href={href!}
              prefetch={false}
              rel="nofollow"
              scroll={false}
              shallow
              tabIndex={linkIsPrimaryInteractive ? undefined : -1}
            >
              <span css={visuallyHidden}>{accessibleLinkName}</span>
            </Link>
            <span css={styles.radioButtonForeground}>
              <RadioWithLabel
                checked={checked}
                disabled={disabled}
                label={labelText}
                name={name}
                onChange={(e) => (onChange ? onChange(radioButtonValue, e) : doNothing())}
                value={radioButtonValue}
              />
            </span>
          </>
        );

        return (
          <div
            css={styles.radioButtonWrapperContainer}
            data-testid={radioButtonValue}
            key={radioButtonValue}
          >
            {linkIsPrimaryInteractive ? (
              <span css={styles.radioButtonRow}>{linkedRadioButton}</span>
            ) : (
              linkedRadioButton
            )}
            {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
          </div>
        );
      },
    )}
  </div>
);
